import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useToast } from '../contexts/ToastContext';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  UserIcon,
  Mail01Icon,
  SmartPhone01Icon,
  Key02Icon,
  Logout01Icon,
  Clock01Icon,
  PencilEdit02Icon,
  Wallet01Icon,
  ArrowRight01Icon,
  Delete02Icon,
  BankIcon,
  PlusSignIcon,
  AlertCircleIcon,
  Notification01Icon,
  CheckmarkCircle01Icon,
  Shield02Icon,
  Copy01Icon,
  Link01Icon
} from '@hugeicons/core-free-icons';
import ResponsiveModal from '../components/ResponsiveModal';
import Button from '../components/Button';
import BankAutocomplete from '../components/BankAutocomplete';
import { api } from '../utils/api';

const Profile = () => {
  const { user, logout, login } = useAuth();
  const { permission, subscribe } = usePushNotifications();
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [derivAccounts, setDerivAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isTelegramModalOpen, setIsTelegramModalOpen] = useState(false);
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  const [telegramConnected, setTelegramConnected] = useState(false);
  const [connectionCode, setConnectionCode] = useState(null);
  const [botUsername, setBotUsername] = useState(null);
  const [agentSlug, setAgentSlug] = useState(null);
  const [newBank, setNewBank] = useState({ bank_name: '', account_number: '', account_name: '' });
  const navigate = useNavigate();

  const fetchProfileAndAccounts = async () => {
    try {
      const response = await api.get('/deriv/my-accounts');
      const { user: apiUser, deriv_account, has_deriv_account } = response.data?.data || response.data;

      setDerivAccounts(deriv_account ? [deriv_account] : []);

      let roleValue = apiUser.role;
      if (typeof roleValue === 'object' && roleValue !== null) {
        roleValue = roleValue.name || roleValue.slug || roleValue.title || 'Unknown';
      }

      const normalizedUser = {
        ...apiUser,
        role: roleValue,
        deriv_linked: has_deriv_account
      };

      const currentToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      if (JSON.stringify(user) !== JSON.stringify(normalizedUser)) {
        login(normalizedUser, currentToken, !!localStorage.getItem('access_token'));
      }

      // Fetch agent profile settings if agent
      if (roleValue === 'agent') {
        fetchAgentProfileSettings();
      }
    } catch (err) {
      console.error('Failed to fetch profile/accounts:', err);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchAgentProfileSettings = async () => {
    try {
      const response = await api.get('/agent-profile/settings');
      const agent = response.data?.data?.agent || response.data?.agent;
      if (agent) {
        setAgentSlug(agent.agent_slug);
        setTelegramEnabled(agent.telegram_notifications_enabled || false);
      }
      
      // Fetch Telegram connection status
      fetchTelegramStatus();
    } catch (err) {
      console.warn('Failed to fetch agent profile settings:', err);
    }
  };

  const fetchTelegramStatus = async () => {
    try {
      const response = await api.get('/telegram/status');
      const { connected, telegram_id, notifications_enabled } = response.data?.data || response.data || {};
      setTelegramConnected(connected);
      setTelegramEnabled(notifications_enabled);
    } catch (err) {
      console.warn('Failed to fetch Telegram status:', err);
    }
  };

  const getConnectionCode = async () => {
    try {
      setSaving(true);
      const response = await api.get('/telegram/connection-code');
      // Handle both nested { data: {...} } and flat responses
      const { token, instructions } = response.data?.data || response.data || {};
      // Extract bot username from instructions or use default
      const botUsername = 'safzi_notify_bot';
      console.log('Telegram connection code generated:', token);
      console.log('Instructions:', instructions);
      setConnectionCode(token);
      setBotUsername(botUsername);
      addToast('Connection code generated!', 'success');
      
      // Start polling for connection status
      pollConnectionStatus();
    } catch (err) {
      console.error('Failed to get connection code:', err);
      const errorMsg = err.message || 'Failed to generate connection code';
      addToast(errorMsg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const pollConnectionStatus = async () => {
    // Poll every 2 seconds for up to 10 minutes (300 attempts)
    let attempts = 0;
    const maxAttempts = 300;
    
    const poll = async () => {
      try {
        const response = await api.get('/telegram/status');
        const { connected } = response.data?.data || response.data || {};
        
        console.log('Polling attempt', attempts + 1, '- connected:', connected);
        
        if (connected) {
          console.log('Telegram connected! Updating UI...');
          setTelegramConnected(true);
          setTelegramEnabled(true);
          setConnectionCode(null);
          setBotUsername(null);
          addToast('Telegram connected successfully!', 'success');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        }
      } catch (err) {
        console.error('Polling error:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        }
      }
    };
    
    poll();
  };

  const handleDisconnectTelegram = async () => {
    if (!window.confirm('Disconnect Telegram? You will stop receiving notifications.')) return;
    setSaving(true);
    try {
      await api.post('/telegram/disconnect');
      setTelegramConnected(false);
      setTelegramEnabled(false);
      setConnectionCode(null);
      setBotUsername(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to disconnect Telegram');
    } finally {
      setSaving(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await api.get('/bank-accounts');
      // Handle both { data: [...] } and potentially nested structures
      const accounts = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setBankAccounts(accounts);
    } catch (err) {
      console.error('Failed to fetch bank accounts:', err);
    } finally {
      setLoadingBanks(false);
    }
  };

  useEffect(() => {
    fetchProfileAndAccounts();
    fetchBankAccounts();
  }, []);

  useEffect(() => {
    // Auto-prefill account name for clients from Deriv
    if (user?.role === 'client' && derivAccounts.length > 0 && isBankModalOpen) {
      setNewBank(prev => ({ ...prev, account_name: derivAccounts[0].fullname }));
    }
  }, [isBankModalOpen, derivAccounts, user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleUnlinkAccount = () => {
    setIsUnlinkModalOpen(true);
  };

  const handleAddBank = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/bank-accounts', newBank);
      await fetchBankAccounts();
      setIsBankModalOpen(false);
      setNewBank({ bank_name: '', account_number: '', account_name: '' });
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBank = async (id) => {
    if (!window.confirm("Delete this bank account?")) return;
    setSaving(true);
    try {
      await api.delete(`/bank-accounts/${id}`);
      await fetchBankAccounts();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTelegram = async () => {
    if (user?.role !== 'agent') return;
    
    // If not connected, show connection code first
    if (!telegramConnected) {
      await getConnectionCode();
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/agent-profile/update', {
        telegram_notifications_enabled: !telegramEnabled
      });
      setTelegramEnabled(!telegramEnabled);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update Telegram settings');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-8 pb-20">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white/10 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/20 relative overflow-hidden">
        <div className="relative z-10">
          <h1 className="text-4xl font-black text-charcoal dark:text-white tracking-tight">Account Profile</h1>
          <p className="text-muted-gray dark:text-gray-300 mt-2 font-medium text-lg">Manage your personal details and linked accounts.</p>
        </div>
        <div className="relative z-10">
          <button
            onClick={handleLogout}
            className="flex items-center text-red-600 hover:text-red-800 dark:hover:text-red-400 font-black bg-white dark:bg-red-900/20 px-8 py-4 rounded-2xl transition-all border border-light-gray/10 dark:border-red-500/20"
          >
            <HugeiconsIcon icon={Logout01Icon} size={20} className="mr-2" />
            Logout Session
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Card */}
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-sm">
            <div className="flex items-center space-x-4 mb-8">
              <div className="bg-soft-green/10 p-4 rounded-full text-soft-green">
                <HugeiconsIcon icon={UserIcon} size={32} />
              </div>
              <div>
                <h3 className="text-2xl font-black text-charcoal dark:text-white">Personal Information</h3>
                <p className="text-sm text-muted-gray font-medium">Your verified identity on Safzi</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <div className="flex items-center gap-2 px-1 text-muted-gray">
                  <HugeiconsIcon icon={UserIcon} size={14} className="text-soft-green" />
                  <span className="text-[10px] font-black uppercase tracking-widest">Full Name</span>
                </div>
                <div className="bg-off-white/50 dark:bg-gray-800/50 border border-light-gray/20 dark:border-gray-700/20 rounded-2xl px-5 py-4 font-bold text-charcoal dark:text-white">
                  {derivAccounts[0]?.fullname || user?.name || 'User'}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 px-1 text-muted-gray">
                  <HugeiconsIcon icon={Key02Icon} size={14} className="text-soft-green" />
                  <span className="text-[10px] font-black uppercase tracking-widest">Account Role</span>
                </div>
                <div className="bg-off-white/50 dark:bg-gray-800/50 border border-light-gray/20 dark:border-gray-700/20 rounded-2xl px-5 py-4 font-bold text-charcoal dark:text-white">
                  {user?.role?.toUpperCase() || 'N/A'}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2 text-muted-gray">
                    <HugeiconsIcon icon={Mail01Icon} size={14} className="text-soft-green" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Email Address</span>
                  </div>
                  {user?.email_verified_at ? (
                    <span className="text-[8px] font-black uppercase bg-green-50 text-green-600 px-2 py-0.5 rounded-full border border-green-100">Verified</span>
                  ) : (
                    <span className="text-[8px] font-black uppercase bg-red-50 text-red-600 px-2 py-0.5 rounded-full border border-red-100">Unverified</span>
                  )}
                </div>
                <div className="bg-off-white/50 dark:bg-gray-800/50 border border-light-gray/20 dark:border-gray-700/20 rounded-2xl px-5 py-4 font-bold text-charcoal dark:text-white truncate">
                  {user?.email}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2 text-muted-gray">
                    <HugeiconsIcon icon={SmartPhone01Icon} size={14} className="text-soft-green" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Phone Number</span>
                  </div>
                  {user?.primary_phone?.is_verified ? (
                    <span className="text-[8px] font-black uppercase bg-green-50 text-green-600 px-2 py-0.5 rounded-full border border-green-100">Verified</span>
                  ) : (
                    <span className="text-[8px] font-black uppercase bg-red-50 text-red-600 px-2 py-0.5 rounded-full border border-red-100">Unverified</span>
                  )}
                </div>
                <div className="bg-off-white/50 dark:bg-gray-800/50 border border-light-gray/20 dark:border-gray-700/20 rounded-2xl px-5 py-4 font-bold text-charcoal dark:text-white">
                  {user?.primary_phone?.full_phone_number || 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Agent Profile Link */}
          {user?.role === 'agent' && agentSlug && (
            <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-6 sm:p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-blue-500/10 p-3 sm:p-4 rounded-full text-blue-500 shrink-0">
                  <HugeiconsIcon icon={Link01Icon} size={28} className="sm:hidden" />
                  <HugeiconsIcon icon={Link01Icon} size={32} className="hidden sm:block" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-black text-charcoal dark:text-white leading-tight">Your Agent Profile</h3>
                  <p className="text-xs sm:text-sm text-muted-gray font-medium">Share your profile link with clients</p>
                </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 sm:p-5 rounded-2xl border border-blue-100 dark:border-blue-700/30">
                <p className="text-[10px] font-black uppercase text-blue-700 dark:text-blue-300 mb-2">Profile Link:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white dark:bg-gray-800 px-3 py-2.5 rounded-xl text-xs sm:text-sm font-mono text-charcoal dark:text-white truncate">
                    {window.location.origin}/agent/{agentSlug}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/agent/${agentSlug}`);
                      addToast('Profile link copied!', 'success');
                    }}
                    className="p-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors shrink-0"
                  >
                    <HugeiconsIcon icon={Copy01Icon} size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bank Accounts Section */}
          <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-6 sm:p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
              <div className="flex items-center space-x-4">
                <div className="bg-purple-500/10 p-3 sm:p-4 rounded-full text-purple-500 shrink-0">
                  <HugeiconsIcon icon={BankIcon} size={28} className="sm:hidden" />
                  <HugeiconsIcon icon={BankIcon} size={32} className="hidden sm:block" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-black text-charcoal dark:text-white leading-tight">Bank Accounts</h3>
                  <p className="text-xs sm:text-sm text-muted-gray font-medium">For deposits and withdrawals</p>
                </div>
              </div>
              <Button onClick={() => setIsBankModalOpen(true)} className="w-full sm:w-auto px-6 py-3.5 rounded-2xl gap-2 font-black text-xs shadow-lg">
                <HugeiconsIcon icon={PlusSignIcon} size={16} />
                Add New Bank
              </Button>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {loadingBanks ? (
                <div className="p-8 text-center animate-pulse text-muted-gray font-bold">Loading banks...</div>
              ) : bankAccounts.length === 0 ? (
                <div className="text-center p-8 bg-off-white/30 dark:bg-gray-800/30 rounded-2xl border border-dashed border-light-gray dark:border-gray-700">
                  <p className="text-muted-gray text-xs sm:text-sm font-medium">No bank accounts added yet.</p>
                </div>
              ) : (
                bankAccounts.map((bank) => (
                  <div key={bank.id} className="flex items-center justify-between p-4 sm:p-5 bg-off-white/50 dark:bg-gray-800/50 rounded-2xl border border-light-gray/20 dark:border-gray-700/20 group">
                    <div className="min-w-0 pr-2">
                      <p className="font-black text-charcoal dark:text-white truncate text-sm sm:text-base">{bank.bank_name}</p>
                      <p className="text-[10px] sm:text-sm font-medium text-muted-gray truncate">{bank.account_number} • {bank.account_name}</p>
                    </div>
                    <button 
                      onClick={() => handleDeleteBank(bank.id)}
                      className="p-2 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors shrink-0 sm:opacity-0 sm:group-hover:opacity-100"
                    >
                      <HugeiconsIcon icon={Delete02Icon} size={18} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Linked Accounts Section */}
          <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-sm">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-10 h-10 rounded-xl overflow-hidden border border-light-gray/20 dark:border-gray-700 shadow-sm">
                <img src="/deriv-logo.jpeg" alt="Deriv" className="w-full h-full object-cover" />
              </div>
              <div>
                <h3 className="text-2xl font-black text-charcoal dark:text-white">Deriv Integration</h3>
                <p className="text-sm text-muted-gray font-medium">Direct wallet synchronization</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {loadingAccounts ? (
                <div className="col-span-full p-12 text-center animate-pulse text-muted-gray font-bold">Syncing accounts...</div>
              ) : derivAccounts.length === 0 ? (
                <div className="col-span-full bg-off-white/50 dark:bg-gray-800/50 p-12 rounded-[2rem] text-center border border-dashed border-light-gray dark:border-gray-700">
                  <p className="text-muted-gray font-bold text-lg mb-4">No Deriv accounts linked.</p>
                  <Button onClick={() => navigate('/link-deriv')} className="px-8 py-3 rounded-2xl">Connect Deriv Now</Button>
                </div>
              ) : (
                derivAccounts.map((acc) => (
                  <div key={acc.deriv_id} className="bg-off-white/50 dark:bg-gray-800/50 p-6 rounded-[2rem] border border-light-gray/20 dark:border-gray-700/20 space-y-6 relative overflow-hidden group">
                    <div className="flex justify-between items-start relative z-10">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-2xl ${acc.account_type === 'demo' ? 'bg-orange-500/10 text-orange-500' : 'bg-soft-green/10 text-soft-green'}`}>
                          <HugeiconsIcon icon={Wallet01Icon} size={24} />
                        </div>
                        <div>
                          <p className="font-black text-charcoal dark:text-white text-lg">{acc.deriv_id}</p>
                          <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded-full ${acc.account_type === 'demo' ? 'bg-orange-100 text-orange-600' : 'bg-green-100 text-green-600'}`}>
                            {acc.account_type}
                          </span>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleUnlinkAccount(acc.deriv_id)}
                        disabled={saving}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                        title="Unlink Account"
                      >
                        <HugeiconsIcon icon={Delete02Icon} size={18} />
                      </button>
                    </div>
                    <div className="relative z-10">
                      <p className="text-muted-gray text-xs font-bold uppercase tracking-widest mb-1">Available Balance</p>
                      <p className="text-3xl font-black text-charcoal dark:text-white">${parseFloat(acc.current_balance || 0).toFixed(2)}</p>
                      <p className="text-xs text-soft-green font-bold">{acc.balance_currency}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Sidebar Actions */}
        <div className="space-y-8">
          <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-sm">
            <h3 className="text-xl font-black text-charcoal dark:text-white mb-6">Security & Activity</h3>
            <div className="space-y-4">
              <button
                onClick={() => setIsNotificationModalOpen(true)}
                className="w-full flex flex-row items-center justify-between p-5 bg-off-white/50 dark:bg-gray-800/50 border border-light-gray/20 dark:border-gray-700/20 rounded-2xl hover:bg-white dark:hover:bg-gray-800 transition-all shadow-sm group text-left"
              >
                <div className="flex items-center min-w-0 mr-2">
                  <HugeiconsIcon icon={Notification01Icon} size={20} className={`mr-3 shrink-0 ${permission === 'granted' ? 'text-soft-green' : 'text-muted-gray group-hover:text-soft-green'}`} />
                  <span className="font-bold text-charcoal dark:text-white truncate">Push Notifications</span>
                </div>
                <div className="shrink-0">
                  {permission === 'granted' ? (
                    <span className="text-[10px] font-black uppercase text-soft-green tracking-widest bg-soft-green/10 px-2 py-1 rounded-lg">Active</span>
                  ) : (
                    <span className="text-[10px] font-black uppercase text-blue-500 tracking-widest bg-blue-500/10 px-2 py-1 rounded-lg group-hover:bg-blue-500 group-hover:text-white transition-colors">Enable</span>
                  )}
                </div>
              </button>

              {/* Telegram Notifications (Agent Only) */}
              {user?.role === 'agent' && (
                <button
                  onClick={() => setIsTelegramModalOpen(true)}
                  className="w-full flex flex-row items-center justify-between p-5 bg-off-white/50 dark:bg-gray-800/50 border border-light-gray/20 dark:border-gray-700/20 rounded-2xl hover:bg-white dark:hover:bg-gray-800 transition-all shadow-sm group text-left"
                >
                  <div className="flex items-center min-w-0 mr-2">
                    <HugeiconsIcon icon={Mail01Icon} size={20} className={`mr-3 shrink-0 ${telegramEnabled ? 'text-blue-500' : 'text-muted-gray group-hover:text-blue-500'}`} />
                    <span className="font-bold text-charcoal dark:text-white truncate">Telegram Notifications</span>
                  </div>
                  <div className="shrink-0">
                    {telegramEnabled ? (
                      <span className="text-[10px] font-black uppercase text-blue-500 tracking-widest bg-blue-500/10 px-2 py-1 rounded-lg">Active</span>
                    ) : (
                      <span className="text-[10px] font-black uppercase text-blue-500 tracking-widest bg-blue-500/10 px-2 py-1 rounded-lg group-hover:bg-blue-500 group-hover:text-white transition-colors">Enable</span>
                    )}
                  </div>
                </button>
              )}

              <button
                onClick={() => navigate('/change-password')}
                className="w-full flex items-center justify-between p-5 bg-off-white/50 dark:bg-gray-800/50 border border-light-gray/20 dark:border-gray-700/20 rounded-2xl hover:bg-white dark:hover:bg-gray-800 transition-all shadow-sm group"
              >
                <div className="flex items-center">
                  <HugeiconsIcon icon={Key02Icon} size={20} className="text-muted-gray group-hover:text-soft-green mr-3" />
                  <span className="font-bold text-charcoal dark:text-white">Security</span>
                </div>
                <HugeiconsIcon icon={PencilEdit02Icon} size={16} className="text-muted-gray" />
              </button>

              <button
                onClick={() => navigate('/sessions')}
                className="w-full flex items-center justify-between p-5 bg-off-white/50 dark:bg-gray-800/50 border border-light-gray/20 dark:border-gray-700/20 rounded-2xl hover:bg-white dark:hover:bg-gray-800 transition-all shadow-sm group"
              >
                <div className="flex items-center">
                  <HugeiconsIcon icon={Clock01Icon} size={20} className="text-muted-gray group-hover:text-soft-green mr-3" />
                  <span className="font-bold text-charcoal dark:text-white">Active Sessions</span>
                </div>
                <HugeiconsIcon icon={ArrowRight01Icon} size={16} className="text-muted-gray" />
              </button>

              {/* Admin Dashboard Quick Link */}
              {(user?.role === 'admin' || user?.role?.name === 'admin' || user?.roles?.some(r => r.name === 'admin')) && (
                <button
                  onClick={() => navigate('/admin')}
                  className="w-full flex items-center justify-between p-5 bg-black dark:bg-white border-none rounded-2xl hover:scale-[1.02] transition-all shadow-lg group"
                >
                  <div className="flex items-center">
                    <HugeiconsIcon icon={Shield02Icon} size={20} className="text-white dark:text-black mr-3" />
                    <span className="font-black text-white dark:text-black uppercase text-xs tracking-widest">Admin Command Center</span>
                  </div>
                  <HugeiconsIcon icon={ArrowRight01Icon} size={16} className="text-white dark:text-black" />
                </button>
              )}
            </div>
          </div>

          <div className="bg-charcoal text-white p-8 rounded-[2.5rem] shadow-float relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-8 opacity-10 transform group-hover:rotate-12 transition-transform duration-500">
              <HugeiconsIcon icon={UserIcon} size={120} />
            </div>
            <div className="relative z-10">
              <h3 className="text-xl font-black mb-2">Need Help?</h3>
              <p className="text-white/80 text-sm font-medium mb-6">Having issues with your account or linking? Our team is ready to assist.</p>
              <button 
                onClick={() => navigate('/support')}
                className="flex items-center gap-2 bg-soft-green hover:bg-light-mint text-charcoal rounded-full px-6 py-3 font-black text-[10px] uppercase tracking-widest transition-colors"
              >
                Contact Support
                <HugeiconsIcon icon={ArrowRight01Icon} size={16} />
              </button>
            </div>
          </div>

          <div className="px-4 text-[10px] text-muted-gray font-black uppercase tracking-[0.2em] space-y-2">
            <p>Member Since: {user?.member_since || formatDate(user?.created_at)}</p>
            <p>Last Login: {formatDate(user?.last_login_at)}</p>
          </div>
        </div>
      </div>

      {/* Add Bank Account Modal */}
      <ResponsiveModal
        isOpen={isBankModalOpen}
        onClose={() => setIsBankModalOpen(false)}
        title="Add Bank Account"
      >
        <form onSubmit={handleAddBank} className="py-4 space-y-4">
          {user?.role === 'client' && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-2xl flex gap-3 border border-yellow-100 dark:border-yellow-700/30">
              <HugeiconsIcon icon={AlertCircleIcon} size={20} className="text-yellow-600 shrink-0" />
              <p className="text-xs text-yellow-800 dark:text-yellow-200 font-medium">
                For your safety, the account name must match your Deriv profile: <b>{derivAccounts[0]?.fullname}</b>.
              </p>
            </div>
          )}

          <div>
            <label className="block text-[10px] font-black text-charcoal dark:text-white uppercase tracking-widest mb-2 px-1">Bank Name</label>
            <BankAutocomplete 
              value={newBank.bank_name}
              onChange={(val) => setNewBank({ ...newBank, bank_name: val })}
              placeholder="Search or type your bank"
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-charcoal dark:text-white uppercase tracking-widest mb-2 px-1">Account Number</label>
            <input 
              required
              maxLength={10}
              value={newBank.account_number}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, ''); // Remove non-digits
                if (val.length <= 10) {
                  setNewBank({ ...newBank, account_number: val });
                }
              }}
              placeholder="e.g. 0123456789"
              className={`w-full px-4 py-4 bg-off-white dark:bg-gray-800 border rounded-2xl focus:ring-2 focus:ring-soft-green outline-none font-bold text-charcoal dark:text-white ${
                newBank.account_number.length > 0 && newBank.account_number.length !== 10 ? 'border-red-500' : 'border-light-gray/20 dark:border-gray-700'
              }`}
            />
            {newBank.account_number.length > 0 && newBank.account_number.length !== 10 && (
              <p className="text-[10px] text-red-500 font-bold mt-1 px-1">Account number must be exactly 10 digits.</p>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-black text-charcoal dark:text-white uppercase tracking-widest mb-2 px-1">Account Name</label>
            <input 
              required
              value={newBank.account_name}
              readOnly={user?.role === 'client'} // Read-only for clients
              onChange={(e) => setNewBank({ ...newBank, account_name: e.target.value })}
              placeholder="e.g. John Doe"
              className={`w-full px-4 py-4 bg-off-white dark:bg-gray-800 border border-light-gray/20 dark:border-gray-700 rounded-2xl focus:ring-2 focus:ring-soft-green outline-none font-bold text-charcoal dark:text-white ${user?.role === 'client' ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
          </div>

          <Button type="submit" disabled={saving || newBank.account_number.length !== 10} className="w-full py-4 rounded-2xl font-black shadow-lg mt-4">
            {saving ? 'Adding...' : 'Save Bank Account'}
          </Button>
        </form>
      </ResponsiveModal>

      {/* Unlink Warning Modal */}
      <ResponsiveModal
        isOpen={isUnlinkModalOpen}
        onClose={() => setIsUnlinkModalOpen(false)}
        title="Unlink Account"
      >
        <div className="py-6 text-center space-y-6">
          <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-full inline-block text-red-500 mb-2">
            <HugeiconsIcon icon={AlertCircleIcon} size={48} />
          </div>
          <h3 className="text-xl font-black text-charcoal dark:text-white">Action Restricted</h3>
          <p className="text-muted-gray px-4 font-medium leading-relaxed text-sm">
            For security reasons, unlinking a Deriv account is disabled. If you need to change your linked wallet, please contact our support team.
          </p>
          <div className="grid grid-cols-1 gap-3 px-4">
            <Button onClick={() => window.location.href = "mailto:support@safzi.com?subject=Unlink Request"} className="w-full py-4 rounded-2xl font-black gap-2 shadow-lg">
              <HugeiconsIcon icon={Mail01Icon} size={20} />
              Contact Support
            </Button>
            <Button variant="secondary" onClick={() => setIsUnlinkModalOpen(false)} className="w-full py-4 rounded-2xl font-bold">Cancel</Button>
          </div>
        </div>
      </ResponsiveModal>

      {/* Push Notification Modal */}
      <ResponsiveModal
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
        title="Push Notifications"
      >
        <div className="py-6 text-center space-y-6">
          <div className={`p-6 rounded-full inline-block mb-2 ${permission === 'granted' ? 'bg-soft-green/10 text-soft-green' : 'bg-blue-500/10 text-blue-500'}`}>
            <HugeiconsIcon icon={permission === 'granted' ? CheckmarkCircle01Icon : Notification01Icon} size={48} />
          </div>

          <div>
            <h3 className="text-2xl font-black text-charcoal dark:text-white">
              {permission === 'granted' ? 'Notifications Active' : 'Stay Updated'}
            </h3>
            <p className="text-muted-gray px-4 mt-2 font-medium leading-relaxed text-sm">
              {permission === 'granted'
                ? "You're all set! You will receive instant alerts for new orders, payments, and account security updates directly on this device."
                : "Enable push notifications to get instant alerts for new orders and payments. Never miss a transaction update."}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 px-4">
            {permission !== 'granted' ? (
              <Button
                onClick={async () => {
                  await subscribe();
                  setIsNotificationModalOpen(false);
                }}
                className="w-full py-4 rounded-2xl font-black gap-2 shadow-lg bg-blue-600 hover:bg-blue-700 text-white"
              >
                Enable Notifications
              </Button>
            ) : (
              <Button
                onClick={() => setIsNotificationModalOpen(false)}
                className="w-full py-4 rounded-2xl font-black shadow-lg"
              >
                Great, thanks!
              </Button>
            )}
            <Button variant="ghost" onClick={() => setIsNotificationModalOpen(false)} className="w-full py-4 font-bold text-muted-gray">
              {permission === 'granted' ? 'Close' : 'Maybe Later'}
            </Button>
          </div>
        </div>
      </ResponsiveModal>

      {/* Telegram Notification Modal */}
      <ResponsiveModal
        isOpen={isTelegramModalOpen}
        onClose={() => setIsTelegramModalOpen(false)}
        onOpen={() => {
          // Generate code when modal opens if not already connected
          if (!telegramConnected && !connectionCode) {
            getConnectionCode();
          }
        }}
        title="Telegram Notifications"
      >
        <div className="py-6 text-center space-y-6">
          <div className={`p-6 rounded-full inline-block mb-2 ${telegramConnected ? 'bg-blue-500/10 text-blue-500' : 'bg-gray-500/10 text-gray-500'}`}>
            <HugeiconsIcon icon={telegramConnected ? CheckmarkCircle01Icon : Mail01Icon} size={48} />
          </div>

          <div>
            <h3 className="text-2xl font-black text-charcoal dark:text-white">
              {telegramConnected ? 'Telegram Connected' : 'Connect Telegram'}
            </h3>
            <p className="text-muted-gray px-4 mt-2 font-medium leading-relaxed text-sm">
              {telegramConnected
                ? "You're receiving order and payout notifications via Telegram. Toggle off to stop receiving Telegram messages."
                : "Enable Telegram notifications to get instant alerts for new orders, payouts, and transaction updates directly in your Telegram inbox."}
            </p>
          </div>

          {!telegramConnected && connectionCode && botUsername && (
            <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-2xl border border-blue-100 dark:border-blue-700/30 text-left">
              <p className="text-xs font-bold text-blue-700 dark:text-blue-300 mb-3">How to connect:</p>
              <ol className="space-y-2 text-sm text-charcoal dark:text-gray-200">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-blue-500">1.</span>
                  <span>
                    <a
                      href={`https://t.me/${botUsername}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >
                      Open @{botUsername} in Telegram
                    </a>{" "}
                    or search for <strong>@{botUsername}</strong>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-blue-500">2.</span>
                  <span>Start a chat with the bot and send:</span>
                </li>
              </ol>
              <div className="flex items-center gap-2 mt-3 bg-white dark:bg-gray-800 p-3 rounded-xl border border-blue-200 dark:border-blue-700/50">
                <code className="flex-1 font-mono text-sm font-bold text-blue-600 dark:text-blue-400 truncate">
                  /connect {connectionCode}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`/connect ${connectionCode}`);
                    alert('Code copied!');
                  }}
                  className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shrink-0"
                >
                  <HugeiconsIcon icon={Copy01Icon} size={16} />
                </button>
              </div>
              <p className="text-xs text-muted-gray mt-3">
                This code expires in 10 minutes. Don't close this modal until connected.
              </p>
            </div>
          )}

          {agentSlug && telegramConnected && (
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-2xl border border-blue-100 dark:border-blue-700/30">
              <p className="text-xs font-bold text-blue-700 dark:text-blue-300 mb-2">Your Agent Profile Link:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg text-xs font-mono truncate">
                  {window.location.origin}/agent/{agentSlug}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/agent/${agentSlug}`);
                    alert('Link copied!');
                  }}
                  className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <HugeiconsIcon icon={Copy01Icon} size={16} />
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 px-4">
            {telegramConnected ? (
              <>
                <Button
                  onClick={async () => {
                    await handleToggleTelegram();
                    setIsTelegramModalOpen(false);
                  }}
                  disabled={saving}
                  className={`w-full py-4 rounded-2xl font-black gap-2 shadow-lg ${
                    telegramEnabled ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {saving ? 'Processing...' : (telegramEnabled ? 'Disable Notifications' : 'Enable Notifications')}
                </Button>
                <Button
                  onClick={handleDisconnectTelegram}
                  disabled={saving}
                  className="w-full py-4 rounded-2xl font-bold gap-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-charcoal dark:text-white"
                >
                  Disconnect Telegram
                </Button>
              </>
            ) : !connectionCode ? (
              <Button
                onClick={getConnectionCode}
                disabled={saving}
                className="w-full py-4 rounded-2xl font-black gap-2 shadow-lg bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? 'Generating Code...' : 'Generate Connection Code'}
              </Button>
            ) : null}
            {(!telegramConnected && connectionCode) && (
              <Button variant="ghost" onClick={() => setIsTelegramModalOpen(false)} className="w-full py-4 font-bold text-muted-gray">
                Close
              </Button>
            )}
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default Profile;
